import React from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../../../elements/ui/card';
import EmptyState from './EmptyState';
import { useTranslation } from 'react-i18next';
import { formatTimeLabel, hasValues, mergeSeries, timeAxisProps, type TimeRange } from './timeSeries';
import type { LineSeries } from '../../../models/time-series';

interface SensorAreaChartProps {
  title: string;
  series: LineSeries[];
  range: TimeRange;
  unit?: string;
}

function CustomTooltip({ active, payload, label, unit }: any) {
  if (!active || !payload?.length) {
    return null;
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-md text-xs">
      <p className="font-medium text-gray-700 mb-1">{formatTimeLabel(label)}</p>
      {payload.map((entry: any) => (
        <div key={entry.name} className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.stroke || entry.color }} />
          <span className="text-gray-600">{entry.name}:</span>
          <span className="font-medium text-gray-900">{entry.value != null ? `${entry.value} ${unit}` : '—'}</span>
        </div>
      ))}
    </div>
  );
}

const SensorAreaChart: React.FC<SensorAreaChartProps> = ({ title, series, range, unit = '°C' }) => {
  const { t } = useTranslation();

  const mergedData = React.useMemo(() => mergeSeries(series, range), [series, range]);
  const hasData = hasValues(series);

  // The last hour that actually reported, not the last hour of the window - the tail of the window
  // is empty whenever a sensor is behind, and reading "—" there says nothing about the pen.
  const currentValues = series.map((s) => {
    const lastReported = [...s.data].reverse().find((p) => p.value != null);

    return { name: s.name, color: s.strokeColor, value: lastReported?.value };
  });

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle>{title}</CardTitle>
          <div className="flex items-center gap-3">
            {currentValues.map((cv) => (
              <div key={cv.name} className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: cv.color }} />
                <span className="text-xs text-gray-500">{cv.name}</span>
                {cv.value != null && (
                  <span className="text-xs font-medium text-gray-700">
                    {cv.value} {unit}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <EmptyState title={`${t('f-no-data')}`} description={`${t('f-sensor-not-available')}`} />
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={mergedData}>
              <defs>
                {series.map((s) => (
                  <linearGradient key={s.name} id={`gradient-${s.name}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={s.strokeColor} stopOpacity={0.2} />
                    <stop offset="95%" stopColor={s.strokeColor} stopOpacity={0.02} />
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis {...timeAxisProps(range)} />
              <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false} width={35} />
              <Tooltip content={<CustomTooltip unit={unit} />} />
              {series.map((s) => (
                <Area
                  key={s.name}
                  type="monotone"
                  dataKey={s.name}
                  stroke={s.strokeColor}
                  strokeWidth={2}
                  fill={`url(#gradient-${s.name})`}
                  dot={false}
                  connectNulls={false}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default SensorAreaChart;
